import { Loader } from '@mantine/core'
import { Dropzone as MantineDropzone } from '@mantine/dropzone'
import { cn } from '@pubstack/ui/utils/cn'
import { useState } from 'react'
import { Colors } from '~/assets/style/colors'
import { Icon } from '~/components/Icon'

export interface DropzoneProps {
  onDrop: (file: File) => void
  accept?: string[]
  currentFile?: File | null
  loading?: boolean
  error?: boolean
  disabled?: boolean
}

interface DropzoneMessageProps {
  primaryText: string
  secondaryText: string
  iconName: 'upload' | 'refresh'
  isError?: boolean
}

function DropzoneText({ primaryText, secondaryText, isError }: Omit<DropzoneMessageProps, 'iconName'>) {
  return (
    <div className="flex flex-1 flex-col text-left">
      <div className={cn('text-base leading-[1.625rem] font-bold', isError ? 'text-cherry' : 'text-jet')}>{primaryText}</div>
      <div className="text-sm leading-[1.375rem] text-jet">{secondaryText}</div>
    </div>
  )
}

function DropzoneMessage({ primaryText, secondaryText, iconName, isError }: DropzoneMessageProps) {
  return (
    <div className="flex w-full items-center justify-between">
      <DropzoneText primaryText={primaryText} secondaryText={secondaryText} isError={isError} />
      <div className={cn('flex size-10 items-center justify-center rounded border bg-white', isError ? 'border-cherry' : 'border-cobalt')}>
        <Icon name={iconName} width="24px" height="24px" fill={isError ? Colors.Cherry : Colors.King} />
      </div>
    </div>
  )
}

function DropzoneLoading() {
  return (
    <div className="flex w-full items-center justify-between">
      <DropzoneText primaryText="Uploading ..." secondaryText="It may take a few seconds. Please wait until the upload is complete." />
      <div className="pr-2.5">
        <Loader color="king" size={'36px'} />
      </div>
    </div>
  )
}

export function Dropzone({ onDrop, accept = ['text/csv'], currentFile, loading = false, error = false, disabled = false }: DropzoneProps) {
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([])

  const handleDrop = (files: File[]) => {
    if (files.length > 0) {
      setRejectedFiles([])
      onDrop(files[0])
    }
  }

  const handleReject = (files: any[]) => {
    const errors = files.map((f) => {
      if (f.errors?.[0]?.code === 'file-invalid-type') {
        return 'Invalid file type. Please upload a CSV file'
      }
      return 'File rejected'
    })
    setRejectedFiles(errors)
  }

  const hasError = rejectedFiles.length > 0 || error
  const isDisabled = loading || disabled

  return (
    <MantineDropzone
      onDrop={handleDrop}
      onReject={handleReject}
      accept={accept}
      multiple={false}
      disabled={isDisabled}
      classNames={{ root: cn('group border-none bg-transparent p-0', isDisabled && 'cursor-not-allowed', disabled && 'opacity-50') }}
    >
      <div
        className={cn(
          'flex items-center justify-between rounded-lg border border-dashed px-4 py-3',
          hasError ? 'border-cherry bg-mew' : 'border-silver',
          'group-data-[accept]:border-king group-data-[accept]:bg-topaze group-data-[reject]:border-cherry group-data-[reject]:bg-mew'
        )}
      >
        <MantineDropzone.Idle>
          {loading ? (
            <DropzoneLoading />
          ) : hasError && currentFile ? (
            <DropzoneMessage primaryText={currentFile.name} secondaryText="Upload failed. Drop a new file to try again." iconName="refresh" isError={true} />
          ) : hasError ? (
            <DropzoneMessage primaryText="Invalid file format" secondaryText="Only CSV files are accepted. Please convert your file and try again." iconName="refresh" isError={true} />
          ) : currentFile ? (
            <DropzoneMessage primaryText={currentFile.name} secondaryText="Drop a file or replace the current file." iconName="refresh" />
          ) : (
            <DropzoneMessage primaryText="Drop your csv file to upload" secondaryText="or click to browse it." iconName="upload" />
          )}
        </MantineDropzone.Idle>

        <MantineDropzone.Accept>
          <DropzoneMessage primaryText="Drop your csv file to upload" secondaryText="or click to browse it." iconName="upload" />
        </MantineDropzone.Accept>
        <MantineDropzone.Reject>
          <DropzoneMessage primaryText="Invalid file format" secondaryText="Only CSV files are accepted. Please convert your file and try again." iconName="refresh" isError={true} />
        </MantineDropzone.Reject>
      </div>
    </MantineDropzone>
  )
}
